/**
 * useDiagramItemDefinition — Step 0 Diagram mode lifecycle.
 *
 * Fetches the project's architecture diagrams once on mount,
 * tracks the user's selection, and on submit fires
 * `startAnalysis('diagram-{diagramId}', ...)` which the backend
 * interprets as "use this diagram's components as the Item
 * Definition seed".
 */
import { useCallback, useEffect, useState } from 'react';
import { exportDiagramAssets, listDiagramsForSession } from '../../../api/diagram';
import { startAnalysis } from '../../../api/documents';
import type { DiagramFlowState, DiagramSummary } from '../step-components/stepBodyProps';
import type { WorkflowActions } from '../state/workflowV2Types';

const UPLOAD_STEP_ID = 0;

export interface UseDiagramItemDefinitionInput {
  sessionId: string | null | undefined;
  tenantId: string | null | undefined;
  actions: WorkflowActions;
  onThreadIdResolved: (threadId: string) => void;
  /** Whether diagram-mode is currently the selected mode — gates the fetch. */
  enabled: boolean;
}

function normaliseDiagrams(raw: unknown): DiagramSummary[] {
  // Backend returns `{ diagrams: [...] }` or a bare array depending
  // on the route. Tolerate both. Each entry has `id` / `_id` /
  // `diagram_id` and a `name` / `title` field; coerce to the shared
  // DiagramSummary shape.
  const list: any[] = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.diagrams)
    ? (raw as any).diagrams
    : [];
  return list.map((d) => ({
    id: String(d?.id ?? d?._id ?? d?.diagram_id ?? ''),
    name: String(d?.name ?? d?.title ?? 'Untitled diagram'),
    updatedAt: d?.updated_at ?? d?.updatedAt ?? null,
  })).filter((d) => d.id);
}

export function useDiagramItemDefinition({
  sessionId,
  tenantId,
  actions,
  onThreadIdResolved,
  enabled,
}: UseDiagramItemDefinitionInput): DiagramFlowState {
  const [diagrams, setDiagrams] = useState<DiagramSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !sessionId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listDiagramsForSession(sessionId)
      .then((resp: any) => {
        if (cancelled) return;
        setDiagrams(normaliseDiagrams(resp));
      })
      .catch((e: any) => {
        if (cancelled) return;
        setLoadError(e?.response?.data?.detail || e?.message || 'Failed to load diagrams');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, sessionId]);

  const onSelect = useCallback((diagramId: string) => {
    setSelectedId(diagramId);
  }, []);

  const onSubmit = useCallback(async () => {
    if (!sessionId || !tenantId) {
      actions.setStepStatus(UPLOAD_STEP_ID, 'failed', 'Missing session or tenant context.');
      return;
    }
    if (!selectedId) {
      setSubmitError('Pick a diagram first.');
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    actions.setStepStatus(UPLOAD_STEP_ID, 'generating');
    try {
      // Export FIRST. `startAnalysis('diagram-…')` reads the item definition out
      // of session metadata, and `export-assets` is the only thing that writes
      // it there. Going straight to startAnalysis left the metadata empty and
      // the backend rejected every diagram-mode run with a 500. The list-page
      // "use for TARA" route always exported first, which is why that path
      // worked and this one never did.
      await exportDiagramAssets(selectedId, {
        session_id: sessionId,
        overwrite_existing: false,
      });

      const startResp: any = await startAnalysis(`diagram-${selectedId}`, sessionId, tenantId);
      const newThreadId = startResp?.thread_id;
      if (newThreadId) onThreadIdResolved(newThreadId);
      actions.setStepStatus(UPLOAD_STEP_ID, 'approved');
      // Optimistic: Step 1's agent has just started on the backend.
      // Without this write, the rail circle stays at "1" until the
      // first polling tick (~3s later) catches up — the user sees a
      // numbered circle instead of the in-flight spinner. Polling
      // will overwrite this with the real backend status (still
      // `generating`) shortly. Parity with `useUploadFlow.ts:65`.
      actions.setStepStatus(1, 'generating');
      actions.selectStep(1);
    } catch (e: any) {
      const message = e?.response?.data?.detail || e?.message || 'Failed to start analysis from diagram';
      setSubmitError(message);
      actions.setStepStatus(UPLOAD_STEP_ID, 'failed', message);
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, tenantId, actions, selectedId, onThreadIdResolved]);

  return {
    diagrams,
    loading,
    loadError,
    selectedId,
    onSelect,
    submitting,
    submitError,
    onSubmit,
  };
}
